import { useCallback, useReducer } from 'react';

import { RenderMode } from '~/components/common/billingPeriods/types';

export type UseBillingPeriodState = {
  cancelUpdateBillingPeriodState: (v: BillingPeriod) => void;
  updatedBillingPeriod: BillingPeriod;
  oldBillingPeriod: BillingPeriod;
  initializeBillingPeriodState: (v: BillingPeriod) => void;
  renderMode: RenderMode;
  resetBillingPeriodState: () => void;
  setBillingPeriod: (v: BillingPeriod) => void;
  setOldBillingPeriod: (v: BillingPeriod) => void;
  setRenderMode: (v: RenderMode) => void;
  isSaveBillingPeriodError: boolean;
  setIsSaveBillingPeriodError: (v: boolean) => void;
  updateBillingPeriodState: (v: Partial<BillingPeriod>) => void;
};

export const initialAuthorizationState: BillingPeriodAuthorization = {
  approvedSessions: '',
  approvedSessionUnits: null,
  authorizationNumber: '',
  endDate: '',
  startDate: '',
  status: null,
};

export const initialBillingPeriodState: BillingPeriod = {
  adminEpisodeExternalId: null,
  authorizationDetails: [],
  coverageExternalId: '',
  createdAt: new Date().toISOString(),
  endDate: null,
  episodeStartDate: '',
  equipPayorExternalId: '',
  equipPayorName: '',
  isAuthRequired: false,
  patientExternalId: '',
  payorName: '',
  payorProgramExternalId: '',
  payorProgramName: '',
  startDate: null,
  treatmentEpisodeId: null,
  treatmentProgramName: '',
};

enum BillingPeriodActionType {
  CREATE,
  UPDATE,
  VIEW,
  CANCEL_EDIT,
  RESET,
  SET_RENDER_MODE,
  SET_SAVE_ERROR,
}

type BillingPeriodAction =
  | { type: BillingPeriodActionType.VIEW; payload: BillingPeriod }
  | { type: BillingPeriodActionType.CREATE; payload: BillingPeriod }
  | { type: BillingPeriodActionType.UPDATE; payload: Partial<BillingPeriod> }
  | {
      type: BillingPeriodActionType.CANCEL_EDIT;
      payload: BillingPeriod;
    }
  | { type: BillingPeriodActionType.RESET }
  | { type: BillingPeriodActionType.SET_RENDER_MODE; payload: RenderMode }
  | { type: BillingPeriodActionType.SET_SAVE_ERROR; payload: boolean };

type BillingPeriodState = {
  updatedBillingPeriod: BillingPeriod;
  oldBillingPeriod: BillingPeriod;
  renderMode: RenderMode;
  isSaveBillingPeriodError: boolean;
};

function billingPeriodReducer(
  state: BillingPeriodState,
  action: BillingPeriodAction,
) {
  switch (action.type) {
    case BillingPeriodActionType.VIEW:
      return {
        ...state,
        oldBillingPeriod: action.payload,
        renderMode: RenderMode.VIEW,
        updatedBillingPeriod: action.payload,
      };
    case BillingPeriodActionType.CREATE:
      return {
        ...state,
        renderMode: RenderMode.CREATE,
        updatedBillingPeriod: action.payload,
      };
    case BillingPeriodActionType.UPDATE:
      return {
        ...state,
        updatedBillingPeriod: {
          ...state.updatedBillingPeriod,
          ...action.payload,
        },
      };
    case BillingPeriodActionType.CANCEL_EDIT:
      return {
        ...state,
        updatedBillingPeriod: action.payload,
      };
    case BillingPeriodActionType.RESET:
      return {
        isSaveBillingPeriodError: false,
        oldBillingPeriod: null,
        renderMode: RenderMode.CREATE,
        updatedBillingPeriod: null,
      };
    case BillingPeriodActionType.SET_RENDER_MODE:
      return { ...state, renderMode: action.payload };
    case BillingPeriodActionType.SET_SAVE_ERROR:
      return { ...state, isSaveBillingPeriodError: action.payload };
    default:
      return state;
  }
}

export const useBillingPeriod = (initialData?: BillingPeriod) => {
  const [
    {
      updatedBillingPeriod,
      oldBillingPeriod,
      renderMode,
      isSaveBillingPeriodError,
    },
    dispatch,
  ] = useReducer(billingPeriodReducer, {
    isSaveBillingPeriodError: false,
    oldBillingPeriod: null,
    renderMode: RenderMode.CREATE,
    updatedBillingPeriod: initialData,
  });

  const setBillingPeriod = useCallback((billingPeriod: BillingPeriod) => {
    dispatch({
      payload: billingPeriod,
      type: BillingPeriodActionType.VIEW,
    });
  }, []);

  const initializeBillingPeriodState = useCallback(
    (newBillingPeriod: BillingPeriod) => {
      dispatch({
        payload: newBillingPeriod,
        type: BillingPeriodActionType.CREATE,
      });
    },
    [],
  );

  const updateBillingPeriodState = useCallback(
    (updatedFields: Partial<BillingPeriod>) => {
      dispatch({
        payload: updatedFields,
        type: BillingPeriodActionType.UPDATE,
      });
    },
    [],
  );

  const setOldBillingPeriod = useCallback((oldBillingPeriod: BillingPeriod) => {
    dispatch({
      payload: oldBillingPeriod,
      type: BillingPeriodActionType.VIEW,
    });
  }, []);

  const cancelUpdateBillingPeriodState = useCallback(
    (oldBillingPeriod: BillingPeriod) => {
      dispatch({
        payload: oldBillingPeriod,
        type: BillingPeriodActionType.CANCEL_EDIT,
      });
    },
    [],
  );

  const setRenderMode = useCallback((mode: RenderMode) => {
    dispatch({ payload: mode, type: BillingPeriodActionType.SET_RENDER_MODE });
  }, []);

  const setIsSaveBillingPeriodError = useCallback((isError: boolean) => {
    dispatch({
      payload: isError,
      type: BillingPeriodActionType.SET_SAVE_ERROR,
    });
  }, []);

  const resetBillingPeriodState = useCallback(() => {
    dispatch({ type: BillingPeriodActionType.RESET });
  }, []);

  return {
    cancelUpdateBillingPeriodState,
    initializeBillingPeriodState,
    isSaveBillingPeriodError,
    oldBillingPeriod,
    renderMode,
    resetBillingPeriodState,
    setBillingPeriod,
    setIsSaveBillingPeriodError,
    setOldBillingPeriod,
    setRenderMode,
    updateBillingPeriodState,
    updatedBillingPeriod,
  };
};
